/**
 * Array of supported language codes in the application.
 * Using 'as const' makes this a readonly tuple type for better type safety.
 */
export const LANGUAGES = ['en', 'fr'] as const;

/**
 * Union type representing valid language codes.
 * Creates a type from the values in the LANGUAGES array ('en' | 'fr').
 * This ensures type safety when specifying languages throughout the application.
 */
export type Language = (typeof LANGUAGES)[number];

/**
 * Constants for the supported languages.
 */
export const FR_LANGUAGE: Language = 'fr';
export const EN_LANGUAGE: Language = 'en';
export const DEFAULT_LANGUAGE: Language = EN_LANGUAGE;

/**
 * Date format patterns for each supported language.
 * - en: Year-Month-Day (yyyy-MMM-dd)
 * - fr: Year-Month-Day (yyyy-MMM-dd)
 */
export const DATE_FORMATS = {
  en: 'yyyy-MMM-dd',
  fr: 'yyyy-MMM-dd',
} as const;

/**
 * Backend error-type slug (the trailing part of the ProblemDetails "type" URI) that signals the user
 * acted on stale data, e.g. an order or group shipment another user had already moved on. The HTTP
 * interceptor watches for this and prompts the user to refresh. Keep in sync with the backend
 * ErrorCatalog "stale-data" entry.
 */
export const STALE_DATA_ERROR_TYPE = 'stale-data';

/**
 * Time format patterns for each supported language.
 * - en: 12-hour clock with AM/PM (hh:mm a)
 * - fr: 24-hour clock (HH:mm)
 */
export const TIME_FORMATS = {
  en: 'hh:mm a',
  fr: 'HH:mm',
} as const;

/**
 * Combined date and time format patterns for each supported language.
 * Follows the same conventions as the individual date and time formats.
 */
export const DATE_TIME_FORMATS = {
  en: 'yyyy-MMM-dd hh:mm a',
  fr: 'yyyy-MMM-dd HH:mm',
} as const;

/**
 * Constants for the icons used in the application.
 */
export const Icons = {
  Overview: 'chart-line',
  RequestAcknowledgedList: 'clipboard-check',
  FollowUpList: 'comment-dots',
  OrderVerificationList: 'prescription',
  ReadyToShipList: 'truck',
  PendingDeliveryList: 'truck-ramp-box',
  PatientPickupList: 'person-carry-box',
  CompletedDeliveryList: 'check-circle',
  CommercializationList: 'dollar-sign',
  ArList: 'file-invoice-dollar',
  TestBillList: 'receipt',
  UserList: 'users-line',
  UserDetails: 'user-circle',
  PharmacyList: 'hospital',
  WorkflowList: 'ballot-check',
  PharmacyDetails: 'hospital',
  Sandbox: 'cube',
  FeatureFlags: 'toggle-on',
  Add: 'plus',
  Trash: 'trash',
  Save: 'save',
  Cancel: 'cancel',
  ArrowLeft: 'arrow-left',
  User: 'user',
  WorkflowDetails: 'ballot',
  DrugList: 'capsules',
  View: 'square-arrow-right',
  Edit: 'file-pen',
  Delete: 'trash',
  Copy: 'copy',
  Error: 'exclamation-triangle',
  Home: 'house',
  Logout: 'arrow-right-from-bracket',
  Retry: 'rotate-left',
  ExclamationTriangle: 'exclamation-triangle',
  PharmacyAdministrator: 'user-shield',
  PharmacyManager: 'hospital-user',
  PharmacyUser: 'user',
  TransportationManager: 'truck',
  Administrator: 'user-shield',
  Language: 'language',
  PatientSearch: 'user-magnifying-glass',
  DriverScheduleList: 'calendar-pen',
  OrderHistory: 'rectangle-history-circle-user',
  PatientProfile: 'id-card-clip',
  GroupAddressBook: 'address-book',
  OtherDrugList: 'pills',
  DeliveryIssuesList: 'clipboard-question',
  Relay: 'building-circle-arrow-right',
  Lock: 'lock-keyhole',
  Unlock: 'unlock-keyhole',
  DataTools: 'toolbox',
  DashboardSettings: 'gauge-high',
  ManagerAnalytics: 'chart-mixed',
  Close: 'close',
  MyDay: 'sun-bright',
  KpiSettings: 'bullseye-arrow',
  RecentPatients: 'clock-rotate-left',
} as const;

/**
 * Icons shown beside each dashboard card on the pharmacy tile availability screen, keyed by the
 * card's stable CardKey. Chosen to match the dashboard design. Any card not listed here falls back
 * to {@link DASHBOARD_CARD_ICON_FALLBACK}, so new cards still render an icon.
 */
export const DASHBOARD_CARD_ICONS: Record<string, string> = {
  REQUESTS_TO_BE_ACKNOWLEDGED: 'clipboard-check',
  ISSUE_TYPES: 'triangle-exclamation',
  FOLLOW_UP: 'phone',
  ORDER_VERIFICATIONS: 'badge-check',
  READY_TO_SHIP: 'truck',
  COURIER_ORDERS_SHIPPING_TODAY: 'truck',
  PENDING_DELIVERY: 'clock',
  PENDING_PICK_UP: 'person-carry-box',
};

/** Icon used for a dashboard card whose key is not in {@link DASHBOARD_CARD_ICONS}. */
export const DASHBOARD_CARD_ICON_FALLBACK = 'table-columns';

/**
 * i18n title key for each dashboard card, keyed by its stable CardKey. This is the SAME key each card
 * renders in its own header, so the customize toggles and the tile-availability screen show a name that
 * stays consistent with the card itself. A card not listed here falls back to its backend-provided name.
 */
export const DASHBOARD_CARD_TITLE_KEYS: Record<string, string> = {
  REQUESTS_TO_BE_ACKNOWLEDGED: 'overview.requests-to-be-acknowledged.title',
  ISSUE_TYPES: 'overview.issue-types.title',
  FOLLOW_UP: 'overview.follow-up.title',
  ORDER_VERIFICATIONS: 'overview.order-verifications.title',
  READY_TO_SHIP: 'overview.ready-to-ship.title',
  COURIER_ORDERS_SHIPPING_TODAY: 'overview.courier.title',
  PENDING_DELIVERY: 'overview.pending-delivery.title',
  PENDING_PICK_UP: 'overview.pending-pick-up.title',
};

/**
 * How many workflow rows the Follow up card shows before the user expands the rest. The backend sends
 * the full (capped) list; this is purely the collapsed display threshold.
 */
export const FOLLOW_UP_CARD_DEFAULT_WORKFLOW_ROWS = 10;

/**
 * How long the dashboard's Refresh button stays disabled after a reload finishes (milliseconds). The
 * window starts once loading is complete, so the button stays greyed out for a moment after the board
 * has fully loaded. It sits on top of the in-flight guard so a user can't spam live (cache-bypassing)
 * reloads. Tune here if it feels too eager or too slow.
 */
export const REFRESH_COOLDOWN_MS = 10000;

/**
 * How often the "Next refresh in m:ss" countdown re-reads the clock to update its label and dial
 * (milliseconds). One second is fine-grained enough for a m:ss display without ticking needlessly
 * often. The remaining time is always recomputed from the next-refresh target, so a slow or throttled
 * tick (e.g. a backgrounded tab) only delays the visual update, never drifts the value.
 */
export const REFRESH_COUNTDOWN_TICK_MS = 1000;

/**
 * Small grace period (milliseconds) added past the moment the dashboard data is due to go stale before
 * the auto-refresh actually fetches. The backend caches the data for one refresh interval, so firing a
 * touch after that window guarantees the cache has expired and the reload returns genuinely fresh data
 * rather than the same cached snapshot. It also acts as the floor between auto-refreshes, so if the data
 * is already overdue (e.g. cached data shown on entry, or clock skew) we retry every couple of seconds
 * instead of in a tight loop.
 */
export const AUTO_REFRESH_DUE_BUFFER_MS = 2000;

/** The two card categories the backend groups dashboard cards into. */
export const DashboardCardType = {
  General: 'GENERAL',
  Manager: 'MANAGER',
} as const;

/**
 * Stable keys that identify each dashboard card. These match the CardKey values the API sends and
 * expects, and are the keys the dashboard data endpoint returns each card's data under. Defined here
 * so components and services refer to a card by name instead of repeating the raw string.
 */
export const DashboardCardKey = {
  RequestsToBeAcknowledged: 'REQUESTS_TO_BE_ACKNOWLEDGED',
  IssueTypes: 'ISSUE_TYPES',
  FollowUp: 'FOLLOW_UP',
  OrderVerifications: 'ORDER_VERIFICATIONS',
  ReadyToShip: 'READY_TO_SHIP',
  CourierOrdersShippingToday: 'COURIER_ORDERS_SHIPPING_TODAY',
  PendingDelivery: 'PENDING_DELIVERY',
  PendingPickUp: 'PENDING_PICK_UP',
} as const;

/**
 * The segment a clicked tile count represents. Travels to the destination list as `?segment=` so the
 * list can label the filter it applied and reproduce the same slice the tile counted.
 */
export const DashboardSegment = {
  Overdue: 'overdue',
  Today: 'today',
  Tomorrow: 'tomorrow',
  Urgent: 'urgent',
  Pending: 'pending',
} as const;

export type DashboardSegment =
  (typeof DashboardSegment)[keyof typeof DashboardSegment];

/** The New / Refill split a Follow Up count can carry into the destination list. */
export const DashboardPatientType = {
  New: 'new',
  Refill: 'refill',
} as const;

export type DashboardPatientType =
  (typeof DashboardPatientType)[keyof typeof DashboardPatientType];

/**
 * Query-param names shared by every tile drill-through link. Named here so the cards that write them
 * and the lists that read them cannot drift apart. `Tab` is the pre-existing param the Regular/Group
 * tab pages already use, reused rather than duplicated.
 */
export const DASHBOARD_DRILL_DOWN_PARAMS = {
  Segment: 'segment',
  From: 'from',
  To: 'to',
  FromTile: 'fromTile',
  PatientType: 'patientType',
  WorkflowId: 'workflowId',
  Tab: 'tab',
  CourierId: 'courierId',
} as const;

export const ENUM_NAMES = {
  Province: 'Province',
  Role: 'Role',
  Courier: 'Courier',
  DispenseType: 'DispenseType',
  WorkflowType: 'WorkflowType',
  SourceSystem: 'SourceSystem',
  OrderRejectReason: 'OrderRejectReason',
  DelayReason: 'DelayReason',
  Gender: 'Gender',
  OnDrugStatus: 'OnDrugStatus',
  Language: 'Language',
  StorageType: 'StorageType',
  NextOrderNotRequiredReason: 'NextOrderNotRequiredReason',
  AdhocOrderReason: 'AdhocOrderReason',
  DeliveryIssueResolution: 'DeliveryIssueResolution',
  OrderTransferReason: 'OrderTransferReason',
  RouteType: 'RouteType',
  CommercializationStatus: 'CommercializationStatus',
  ARStatus: 'ARStatus',
  SubmissionType: 'SubmissionType',
} as const;

/**
 * Dispense type values that are considered "locked" for commercialization.
 * Products with these dispense types cannot have their dispense type changed,
 * cannot be selected for matching, and cannot be edited or deleted.
 */
export const LOCKED_DISPENSE_TYPES = [
  'COMMERCIAL',
  'SUBMIT_TO_PROGRAM',
] as const;

export type EnumName = (typeof ENUM_NAMES)[keyof typeof ENUM_NAMES];

export const ROLE_IDS = {
  ADMINISTRATOR: 0,
  PHARMACY_ADMINISTRATOR: 1,
  PHARMACY_MANAGER: 2,
  PHARMACY_USER: 3,
  TRANSPORTATION_MANAGER: 4,
} as const;

export type RoleId = (typeof ROLE_IDS)[keyof typeof ROLE_IDS];

// Extract the role names as a union type
export type RoleName = keyof typeof ROLE_IDS;

/**
 * Constants for the order statuses used in the application.
 */
export const ORDER_STATUS = {
  WaitingToBeAccepted: 'WAITING_TO_BE_ACCEPTED',
  ScreeningIncomplete: 'SCREENING_INCOMPLETE',
  ReadyToBeVerified: 'READY_TO_BE_VERIFIED',
  PendingMedicationDispense: 'PENDING_MEDICATION_DISPENSE',
  PendingFollowUp: 'PENDING_FOLLOW_UP',
  Issue: 'ISSUE',
  RxVerified: 'RX_VERIFIED',
  Shipped: 'SHIPPED',
  WaitingForPickUp: 'WAITING_FOR_PICK_UP',
  IssueRx: 'ISSUE_RX',
  IssueCoverage: 'ISSUE_COVERAGE',
  IssueNoRefillRemaining: 'ISSUE_NO_REFILL_REMAINING',
  IssueDoseChange: 'ISSUE_DOSE_CHANGE',
  IssueAR: 'ISSUE_AR',
  IssueClinical: 'ISSUE_CLINICAL',
  IssueReschedule: 'ISSUE_RESCHEDULE',
  IssueToBeProcessed: 'ISSUE_TO_BE_PROCESSED',
  IssueInventory: 'ISSUE_INVENTORY',
  IssueDispenseError: 'ISSUE_DISPENSE_ERROR',
  Delivered: 'DELIVERED',
  PickedUp: 'PICKED_UP',
  Cancelled: 'CANCELLED',
  PendingKrollAdtCompletion: 'PENDING_KROLL_ADT_COMPLETION',
  WaitingToBeProcessedInKroll: 'WAITING_TO_BE_PROCESSED_IN_KROLL',
  DeliveryIssue: 'DELIVERY_ISSUE',
  OrderRejected: 'ORDER_REJECTED',
  Completed: 'COMPLETED',
} as const;

/**
 * The issue statuses that carry a colour, mapped to their shared --issue-colour-* /
 * --issue-on-* token name (the ISSUE_ status code with its prefix stripped).
 * Single source for every surface that paints an issue colour: the order status badge,
 * the dashboard issue-types card, and the order progress tracker.
 */
export const ISSUE_STATUS_TOKENS: Record<string, string> = {
  [ORDER_STATUS.IssueRx]: 'rx',
  [ORDER_STATUS.IssueCoverage]: 'coverage',
  [ORDER_STATUS.IssueAR]: 'ar',
  [ORDER_STATUS.IssueClinical]: 'clinical',
  [ORDER_STATUS.IssueNoRefillRemaining]: 'no-refill-remaining',
  [ORDER_STATUS.IssueDoseChange]: 'dose-change',
  [ORDER_STATUS.IssueReschedule]: 'reschedule',
  [ORDER_STATUS.IssueToBeProcessed]: 'to-be-processed',
  [ORDER_STATUS.IssueInventory]: 'inventory',
  [ORDER_STATUS.IssueDispenseError]: 'dispense-error',
};

/**
 * Constants for the order grid pages used in the application.
 */
export const OrderGridPage = {
  RequestAcknowledgedList: 'RequestAcknowledgedList',
  FollowUpList: 'FollowUpList',
  OrderVerificationList: 'OrderVerificationList',
  PickUpList: 'PickUpList',
  PendingDeliveryList: 'PendingDeliveryList',
} as const;

export const GridPageSize = {
  PageSizes: [5, 10, 20, 30, 40, 50],
  DefaultPageSize: 20,
};

export const EnumLanguage = {
  English: 'ENGLISH',
  French: 'FRENCH',
} as const;

export const StepCode = {
  Schedule: 'SCHEDULE',
  VerifyRx: 'VERIFY_RX',
  VerifyPackage: 'VERIFY_PACKAGE',
  PendingPickUp: 'PENDING_PICK_UP',
  PickedUp: 'PICKED_UP',
  Ship: 'SHIP',
  Delivered: 'DELIVERED',
} as const;

export const ShipmentType = {
  Delivery: 'DELIVERY',
  PickUp: 'PATIENT_PICK_UP',
} as const;

export const DailyCapacityType = {
  NumberOfShipments: 'NUMBER_OF_SHIPMENTS',
  NumberOfAmbientOrders: 'NUMBER_OF_AMBIENT_ORDERS',
  NumberOfColdChainOrders: 'NUMBER_OF_COLD_CHAIN_ORDERS',
} as const;

export const DeliveryType = {
  Regular: 'REGULAR_DELIVERY',
  Group: 'GROUP_DELIVERY',
} as const;

export const EnumCourier = {
  SentrexDriver: 'SENTREX_DRIVER',
} as const;

export const DrugStorageType = {
  Ambient: 'AMBIENT',
  ColdChain: 'COLD_CHAIN',
} as const;

/**
 * Constants for the group shipment statuses used in the application.
 */
export const GROUP_SHIPMENT_STATUS = {
  PendingOrders: 'PENDING_ORDERS',
  GroupVerified: 'GROUP_VERIFIED',
  Shipped: 'SHIPPED',
  Delivered: 'DELIVERED',
  DeliveryIssue: 'DELIVERY_ISSUE',
} as const;

/**
 * Constants for the commercialization statuses used in the application.
 */
export const COMMERCIALIZATION_STATUS = {
  Commercialized: 'COMMERCIALIZED',
} as const;

export const COMMERCIALIZATION_STATUS_ID = {
  COMMERCIALIZED: 6,
} as const;

/**
 * Constants for the delay reasons used in the application.
 */
export const DelayReason = {
  AnotherFollowUpRequired: 'ANOTHER_FOLLOW_UP_REQ',
  IssueCoverage: 'ISSUE_COVERAGE',
  IssueRx: 'ISSUE_RX',
  IssueDoseChange: 'ISSUE_DOSE_CHANGE',
  IssueAR: 'ISSUE_AR',
  IssueClinical: 'ISSUE_CLINICAL',
  IssueNoRefillRemaining: 'ISSUE_NO_REFILL_REMAINING',
  IssueReschedule: 'ISSUE_RESCHEDULE',
  TreatmentOnHold: 'TREATMENT_ON_HOLD',
  ExtraSupplyOnHand: 'EXTRA_SUPPLY_ON_HAND',
  IssueToBeProcessed: 'ISSUE_TO_BE_PROCESSED',
} as const;

/**
 * Date format placeholder for the Kendo datepicker.
 * This is used to format the datepicker placeholder in the application.
 */
export const DATE_FORMAT_PLACEHOLDER = {
  year: 'yyyy',
  month: 'MMM',
  day: 'dd',
};

/**
 * Constants for the Ad Hoc Workflow.
 */
export const AD_HOC_WORKFLOW_ID = -1;
export const AD_HOC_WORKFLOW_NAME = 'Ad hoc';

/**
 * Constants for the address types
 */
export const ADDRESS_TYPE = {
  Home: 'HOME',
  Alternate: 'ALTERNATE',
} as const;

/**
 * Constants for the phone types
 */
export const PHONE_TYPE = {
  Home: 'HOME',
  Work: 'WORK',
  Cell: 'CELL',
} as const;

export const DAYS_SINCE_FILL = {
  Default: 14,
};

export const ORDER_CREATED_REASON = {
  DeliveryIssue: 'DELIVERY_ISSUE',
  NextOrder: 'NEXT_ORDER',
};

/**
 * Constants for the document type display names
 */
export const DOCUMENT_TYPE_NAMES = {
  1: 'Personalized Letter',
  2: 'Rx',
  3: 'Coverage Info',
  4: 'General',
  5: 'HCP Letter',
} as const;

// The constant for the order attachment note message
export const ATTACHMENTS_RECEIVED_NOTE = 'Attachments received';

/**
 * Required email domain for user registration
 */
export const REQUIRED_EMAIL_DOMAIN = 'sentrex.com';

/**
 * MIME types mapping for file types
 * Maps file extensions (uppercase) to their corresponding MIME types
 */
export const MIME_TYPES: { [key: string]: string } = {
  // Generic/Fallback
  UNDEFINED: 'application/octet-stream',

  // Documents
  PDF: 'application/pdf',
  DOC: 'application/msword',
  DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  XLS: 'application/vnd.ms-excel',
  XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  PPT: 'application/vnd.ms-powerpoint',
  PPTX: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',

  // Images
  PNG: 'image/png',
  JPG: 'image/jpeg',
  JPEG: 'image/jpeg',
  GIF: 'image/gif',
  BMP: 'image/bmp',
  WEBP: 'image/webp',
  SVG: 'image/svg+xml',
  TIFF: 'image/tiff',
  TIF: 'image/tiff',

  // Text
  TXT: 'text/plain',
  CSV: 'text/csv',
  HTML: 'text/html',
  HTM: 'text/html',
  XML: 'application/xml',
  JSON: 'application/json',

  // Archives
  ZIP: 'application/zip',
  RAR: 'application/x-rar-compressed',
  '7Z': 'application/x-7z-compressed',
  TAR: 'application/x-tar',
  GZ: 'application/gzip',

  // Other
  RTF: 'application/rtf',
} as const;

export const SOURCE_SYSTEM = {
  LUMINAV360: 1,
  PRO: 2,
  LUMINAVNEXT: 3,
} as const;

/**
 * Note message for DDI review completion.
 */
export const DDI_REVIEW_COMPLETED_NOTE = 'DDI review completed';

/**
 * Note message auto-appended when an order is successfully verified through the
 * standard (Kroll-connected) process.
 */
export const ORDER_VERIFIED_NOTE = 'Order Verified.';

/**
 * Note message auto-appended when an order is successfully verified manually
 * because the Kroll connection was unavailable. Ticket 35152.
 */
export const MANUAL_ORDER_VERIFIED_NOTE =
  'Kroll connection not available. Order manually verified.';

/**
 * Note message for Initial Counsel.
 */
export const INITIAL_COUNSEL_NOTE_PREFIX = 'Initial Counsel: ';

/**
 * Note pre-filled when a commercialization record is worked. Fixed literal (not
 * translated) for a consistent audit trail. Ticket 34556.
 */
export const DEFAULT_COMMERCIALIZATION_NOTE = 'File reviewed/test billed';

/**
 * System note appended whenever a user refreshes the patient header from Kroll.
 * Fixed literal (not translated) for a consistent audit trail. Ticket 34547.
 */
export const PATIENT_HEADER_REFRESHED_FROM_KROLL_NOTE =
  'Patient header refreshed from Kroll';

/**
 * Constants for the issue types used in the application.
 * Add more as needed.
 */
export const IssueType = {
  TransferToExternalPharmacy: 'TRANSFER_TO_EXTERNAL_PHARM',
  WaybillCorrection: 'ISSUE_WAYBILL_CORRECTION',
} as const;

export const ADDRESS_SELECTION_TYPE = {
  Kroll: 'KROLL',
  Relay: 'RELAY',
};

/**
 * List of order statuses considered "open" in the application.
 * Used to determine if an order is open or not.
 */
export const OpenOrderStatuses = [
  ORDER_STATUS.WaitingToBeAccepted,
  ORDER_STATUS.WaitingToBeProcessedInKroll,
  ORDER_STATUS.ScreeningIncomplete,
  ORDER_STATUS.PendingMedicationDispense,
  ORDER_STATUS.PendingFollowUp,
  ORDER_STATUS.Issue,
  ORDER_STATUS.ReadyToBeVerified,
  ORDER_STATUS.Shipped,
  ORDER_STATUS.WaitingForPickUp,
  ORDER_STATUS.RxVerified,
  ORDER_STATUS.IssueRx,
  ORDER_STATUS.IssueCoverage,
  ORDER_STATUS.IssueNoRefillRemaining,
  ORDER_STATUS.IssueDoseChange,
  ORDER_STATUS.IssueAR,
  ORDER_STATUS.IssueClinical,
  ORDER_STATUS.IssueReschedule,
  ORDER_STATUS.PendingKrollAdtCompletion,
] as const;

/**
 * List of order statuses considered not open in the application.
 */
export const NonOpenOrderStatuses = [
  ORDER_STATUS.Delivered,
  ORDER_STATUS.PickedUp,
  ORDER_STATUS.Cancelled,
  ORDER_STATUS.OrderRejected,
  ORDER_STATUS.Completed,
] as const;

export const WorkflowType = {
  PSP: 'PSP',
  CS: 'CS',
  Pharmacy: 'PHARMACY',
};

/*
 * List of order statuses that are for the follow up list.
 */
export const FollowUpListOrderStatuses = [
  ORDER_STATUS.ScreeningIncomplete,
  ORDER_STATUS.PendingMedicationDispense,
  ORDER_STATUS.PendingFollowUp,
  ORDER_STATUS.Issue,
  ORDER_STATUS.IssueRx,
  ORDER_STATUS.IssueCoverage,
  ORDER_STATUS.IssueNoRefillRemaining,
  ORDER_STATUS.IssueDoseChange,
  ORDER_STATUS.IssueAR,
  ORDER_STATUS.IssueClinical,
  ORDER_STATUS.IssueReschedule,
] as const;

export const FollowUpListDateSearchTypes = {
  DueToday: 'DUE_TODAY',
  DueFrom: 'DUE_FROM',
};

export const AR_STATUS = {
  Paid: 'PAID',
} as const;
